import type {
    NavigationScreenIntegrationState,
    NavigationScreenIntegrationsState,
    NavigationScreenSiteIntegrationsModalState,
    NavigationScreenSpaceIntegrationsModalState,
} from '@gitbook/app-navigation';

import { useNavigationState } from '../../../navigation';

/**
 * Hook returning the screen state with an organization for the integrations screen.
 */
export function useIntegrationsScreen(): NavigationScreenIntegrationsState & {
    organization: string;
} {
    const state = useNavigationState();
    if (state.screen.type !== 'integrations') {
        throw new Error(
            'The useIntegrationsScreen hook can only be used on the integrations screen.'
        );
    }
    if (!state.screen.organization) {
        throw new Error(
            'The useIntegrationsScreen hook can only be used within IntegrationsScreenWithOrganization.'
        );
    }
    return { ...state.screen, organization: state.screen.organization };
}

/**
 * Hook returning the screen state with an organization for the integration screen.
 */
export function useIntegrationScreen(): NavigationScreenIntegrationState & {
    organization: string;
} {
    const state = useNavigationState();
    if (state.screen.type !== 'integration') {
        throw new Error(
            'The useIntegrationScreen hook can only be used on the integration screen.'
        );
    }
    if (!state.screen.organization) {
        throw new Error(
            'The useIntegrationScreen hook can only be used within IntegrationScreenWithOrganization.'
        );
    }
    return { ...state.screen, organization: state.screen.organization };
}

/**
 * Hook returning the screen state for the integrations modal in a space screen.
 */
export function useIntegrationsModalSpaceScreen(): NavigationScreenSpaceIntegrationsModalState {
    const state = useNavigationState();
    if (state.screen.type !== 'space') {
        throw new Error(
            'The useIntegrationsModalSpaceScreen hook can only be used on a space screen.'
        );
    }
    if (state.screen.sideSheet?.type !== 'integrations') {
        throw new Error(
            'The useIntegrationsModalSpaceScreen hook can only be used in the integrations modal on a space screen.'
        );
    }
    return { ...state.screen, sideSheet: state.screen.sideSheet };
}

/**
 * Hook returning the screen state for the integrations modal in a site screen.
 */
export function useIntegrationsModalSiteScreen(): NavigationScreenSiteIntegrationsModalState {
    const state = useNavigationState();
    if (state.screen.type !== 'site') {
        throw new Error(
            'The useIntegrationsModalSiteScreen hook can only be used on a site screen.'
        );
    }
    if (state.screen.sideSheet?.type !== 'integrations') {
        throw new Error(
            'The useIntegrationsModalSiteScreen hook can only be used in the integrations modal on a site screen.'
        );
    }
    return { ...state.screen, sideSheet: state.screen.sideSheet };
}
